import { CircleHelp } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Message } from '@/components/chat/chat-types';
import type { UniversalChatContextPayload } from '@/components/chat/universal-chat-context';
import { SummarizerActionControls } from '@/components/summarizer/SummarizerActionControls';
import { DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import InlineTweet from '@/domains/timeline/components/InlineTweet';
import type { LinkMetadata } from '@/lib/fetch-metadata';
import { collectTweetSummaryImages, limitSummaryImages, MAX_TWEET_SUMMARY_IMAGES } from '@/lib/media/summary-images';
import type { TweetMedia, TweetUrl } from '@/lib/tweet-parser';
import { toLinkMetadataRecord } from '@/lib/url-metadata/normalize';
import { cn } from '@/lib/utils';
import type { TweetEntities, TweetMetrics, Tweet as TweetType } from '@/types/tweet';
import type { MediaMetadataEntry } from '@/types/tweet/media';
import type { AlgorithmicSuggestionCanonical } from '@/types/tweet/tweet';
import { buildMentionProfileLookup } from './mention-profile';
import { QuoteTweet } from './QuoteTweet';
import { useTweetSummary } from './summary/useTweetSummary';
import { type TweetActionStates, TweetActions } from './TweetActions';
import { TweetAvatar } from './TweetAvatar';
import { TweetBody } from './TweetBody';
import { TweetHeader } from './TweetHeader';
import { TweetPinnedBadge } from './TweetPinnedBadge';
import { TweetSummarySection } from './TweetSummarySection';

const SUGGESTION_COPY: Record<AlgorithmicSuggestionCanonical, { label: string; description: string }> = {
  inNetwork: {
    label: 'From people you follow',
    description: 'Twitter surfaced this tweet because it is from someone you already follow.',
  },
  outOfNetwork: {
    label: 'Recommended for you',
    description: 'Twitter suggested this tweet from outside your network based on your activity.',
  },
  promoted: {
    label: 'Promoted',
    description: 'This tweet was marked as promoted content by Twitter.',
  },
  organic: {
    label: 'Organic recommendation',
    description: 'Twitter recommended this tweet organically without paid placement.',
  },
  unknown: {
    label: 'Algorithmic recommendation',
    description: 'Twitter did not provide a specific reason for showing this tweet.',
  },
};

interface TweetHandlers {
  copyLink: () => void;
  reply: () => void;
  retweet: () => void;
  share: () => void;
  toggleBookmark: () => void;
  toggleLike: () => void;
  toggleMute: () => void;
}

interface TweetContentProperties {
  appendMessages: (messages: Message[]) => void;
  canOpenConversation?: boolean;
  cardUrls: TweetUrl[];
  className: string;
  compact?: boolean;
  conversationCount?: number | null;
  defaultExpandText?: boolean;
  disableHover: boolean;
  disableLinkPreviews?: boolean;
  enableActions: boolean;
  handlers: TweetHandlers;
  highlightCardUrl?: string;
  inlineTweetUrls: Array<{ url: string; username: string; tweetId: string }>;
  isAdmin: boolean;
  isChatEnabled: boolean;
  isMuted: boolean;
  longFormHint?: boolean;
  mediaRenderMode?: 'interactive' | 'static';
  metrics: TweetMetrics;
  onBlurCapture?: React.FocusEventHandler<HTMLDivElement>;
  onFocusCapture?: React.FocusEventHandler<HTMLDivElement>;
  onFullTweetLoaded?: (payload: {
    text: string;
    entities?: TweetEntities;
    mediaItems?: TweetMedia[];
    metadata?: Record<string, LinkMetadata>;
    mediaMetadata?: MediaMetadataEntry[];
  }) => void;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  onOpenConversation: () => void;
  onRefreshTweet?: () => void;
  openChat: () => void;
  parsedText: string;
  powerUserEnabled?: boolean;
  quotedTweetId?: string;
  registerContext: (context: UniversalChatContextPayload) => void;
  showPinnedBadge?: boolean;
  states: TweetActionStates;
  tweet: TweetType;
}

export function TweetContent({
  tweet,
  parsedText,
  cardUrls,
  inlineTweetUrls,
  quotedTweetId,
  metrics,
  states,
  handlers,
  enableActions,
  disableHover,
  isMuted,
  isAdmin,
  className,
  onOpenConversation,
  onRefreshTweet,
  longFormHint,
  onFullTweetLoaded,
  disableLinkPreviews,
  highlightCardUrl,
  mediaRenderMode = 'interactive',
  showPinnedBadge,
  canOpenConversation = false,
  conversationCount,
  onMouseEnter,
  onMouseLeave,
  onFocusCapture,
  onBlurCapture,
  compact = false,
  powerUserEnabled = false,
  defaultExpandText = false,
  openChat,
  registerContext,
  isChatEnabled,
  appendMessages,
}: TweetContentProperties) {
  const inlineTweetUrlsForBody = useMemo(
    () =>
      inlineTweetUrls
        .filter((url) => url.tweetId !== tweet.id && (quotedTweetId === undefined || url.tweetId !== quotedTweetId))
        .map((url) => ({ tweetId: url.tweetId, url: url.url })),
    [inlineTweetUrls, tweet.id, quotedTweetId]
  );

  const metadataRecord = useMemo(
    () => toLinkMetadataRecord(tweet.metadata ?? undefined) ?? undefined,
    [tweet.metadata]
  );

  const quotedTweetForDisplay = useMemo(() => {
    const quoted = tweet.quotedTweet;
    if (!quoted) {
      return null;
    }
    const normalizedMetadata = toLinkMetadataRecord(quoted.metadata ?? undefined) ?? undefined;
    return {
      ...quoted,
      metadata: normalizedMetadata,
    } as TweetType & { metadata?: Record<string, LinkMetadata> };
  }, [tweet.quotedTweet]);

  const summaryImagesForTweet = useMemo(
    () => limitSummaryImages(collectTweetSummaryImages(tweet), MAX_TWEET_SUMMARY_IMAGES),
    [tweet]
  );
  const allowAdvancedSummaryOptions = powerUserEnabled || isAdmin;

  const algorithmicMeta = tweet.algorithmic;
  const suggestionCopy = algorithmicMeta?.suggestionType
    ? SUGGESTION_COPY[algorithmicMeta.suggestionType.canonical]
    : null;
  const suggestionTooltip = useMemo(() => {
    if (!suggestionCopy) {
      return null;
    }
    const fetchedAt = algorithmicMeta?.fetchedAt ? new Date(algorithmicMeta.fetchedAt) : null;
    const fetchedLabel = fetchedAt && !Number.isNaN(fetchedAt.valueOf()) ? fetchedAt.toLocaleString() : null;
    const sourceLabel = algorithmicMeta?.source;
    return {
      title: suggestionCopy.label,
      description: suggestionCopy.description,
      fetchedLabel,
      sourceLabel,
    };
  }, [algorithmicMeta, suggestionCopy]);

  const mentionProfileLookup = useMemo(() => buildMentionProfileLookup(tweet), [tweet]);

  const {
    summaryState,
    explanationState,
    isSummarizing,
    isExplaining,
    canSummarize,
    canExplain,
    shouldRenderSummaryControls,
    summarizeButtonLabel,
    explain,
    settingsOpen,
    openSettings,
    closeSettings,
    draftSummaryLengthIndex,
    setDraftSummaryLengthIndex,
    draftModel,
    setDraftModel,
    canonicalDraftModel,
    draftCustomPrompt,
    setDraftCustomPrompt,
    draftSummarySearchEnabled,
    setDraftSummarySearchEnabled,
    supportsSummarySearch,
    liveSearchProviderName,
    providerHandlesVideo,
    submitSettings,
    handleShowMoreVisibility,
    chatFromSummary,
    summaryMode,
    draftSummaryMode,
    setDraftSummaryMode,
    runActiveMode,
    showMoreVisible,
  } = useTweetSummary({
    tweet,
    metrics,
    longFormHint: Boolean(longFormHint),
    summaryImages: summaryImagesForTweet,
    inlineTweetUrls,
    isChatEnabled,
    openChat,
    registerContext,
    appendMessages,
  });

  const conversationBlockersReference = useRef<Set<string>>(new Set());
  const [isConversationBlocked, setIsConversationBlocked] = useState(false);

  const updateConversationBlocker = useCallback((token: string, active: boolean) => {
    const blockers = conversationBlockersReference.current;
    if (active) {
      if (blockers.has(token)) {
        return;
      }
      const next = new Set(blockers);
      next.add(token);
      conversationBlockersReference.current = next;
      setIsConversationBlocked(true);
      return;
    }

    if (!blockers.has(token)) {
      return;
    }

    const next = new Set(blockers);
    next.delete(token);
    conversationBlockersReference.current = next;
    setIsConversationBlocked(next.size > 0);
  }, []);

  const isPrimaryActionRunning = summaryMode === 'explain' ? isExplaining : isSummarizing;

  const handlePrimarySummaryAction = useCallback(async () => {
    if (runActiveMode) {
      await runActiveMode();
    }
  }, [runActiveMode]);

  const handlePrimarySummaryButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      handlePrimarySummaryAction().catch(() => {
        /* ignore summarize errors */
      });
    },
    [handlePrimarySummaryAction]
  );

  const handleSummaryOptionsMenuSelect = useCallback(
    (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (isPrimaryActionRunning) {
        return;
      }
      openSettings();
    },
    [isPrimaryActionRunning, openSettings]
  );

  const shouldRenderInlineSummaryControls = shouldRenderSummaryControls && (Boolean(longFormHint) || showMoreVisible);

  const inlineSummaryControls = shouldRenderInlineSummaryControls ? (
    <SummarizerActionControls
      label={summarizeButtonLabel}
      onPrimaryClick={handlePrimarySummaryButtonClick}
      disabled={isPrimaryActionRunning}
      className="ml-auto"
      secondaryAction={
        allowAdvancedSummaryOptions
          ? {
              type: 'menu',
              ariaLabel: 'Open summarizer options',
              disabled: isPrimaryActionRunning,
              renderMenu: () => (
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    disabled={isPrimaryActionRunning}
                    onSelect={(event) => {
                      handleSummaryOptionsMenuSelect(event);
                    }}
                  >
                    Summarize…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              ),
            }
          : null
      }
    />
  ) : null;

  const handleRootClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!canOpenConversation) {
        return;
      }
      if (isConversationBlocked || conversationBlockersReference.current.size > 0) {
        return;
      }

      const targetNode = event.target;
      if (!(targetNode instanceof HTMLElement)) {
        onOpenConversation();
        return;
      }
      const interactive =
        'a, button, [role="button"], [role="link"], [data-media-container], .tweet-media, svg, [data-no-conversation]';
      const interactiveAncestor = targetNode.closest(interactive);
      if (
        (interactiveAncestor && interactiveAncestor !== event.currentTarget) ||
        ['A', 'BUTTON', 'IMG', 'VIDEO', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL'].includes(targetNode.tagName)
      ) {
        return;
      }

      onOpenConversation();
    },
    [canOpenConversation, isConversationBlocked, onOpenConversation]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!canOpenConversation) {
        return;
      }
      if (isConversationBlocked || conversationBlockersReference.current.size > 0) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpenConversation();
      }
    },
    [canOpenConversation, isConversationBlocked, onOpenConversation]
  );

  const interactiveProperties = canOpenConversation
    ? {
        onClick: handleRootClick,
        onKeyDown: handleKeyDown,
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': 'View conversation',
      }
    : {
        role: 'group' as const,
      };

  const hoverProperties = {
    ...(onMouseEnter ? { onMouseEnter } : {}),
    ...(onMouseLeave ? { onMouseLeave } : {}),
    ...(onFocusCapture ? { onFocusCapture } : {}),
    ...(onBlurCapture ? { onBlurCapture } : {}),
  };

  const author = tweet.author ?? null;
  if (!author) {
    return null;
  }

  const openConversationHandler = canOpenConversation ? onOpenConversation : undefined;
  const handleExplain = () => {
    explain().catch(() => {
      /* ignore explain errors */
    });
  };

  const hasLongFormHint = Boolean(longFormHint);
  const disableLinkPreviewsFlag = Boolean(disableLinkPreviews);

  const tweetBodyProps: Parameters<typeof TweetBody>[0] = {
    parsedHtml: parsedText,
    cardUrls,
    inlineTweetUrls: inlineTweetUrlsForBody,
    className: cn(compact ? 'mt-2' : ''),
    compact,
    tweetId: tweet.id,
    longFormHint: hasLongFormHint,
    disableLinkPreviews: disableLinkPreviewsFlag,
    renderQuotedFallback: !tweet.quotedTweet,
    inlineTweetComponent: InlineTweet,
    onShowMoreVisibilityChange: handleShowMoreVisibility,
    defaultExpanded: defaultExpandText,
    footerSlot: inlineSummaryControls,
    mediaRenderMode,
    onMediaViewerToggle: (active) => {
      updateConversationBlocker('tweet-media', active);
    },
  };
  if (tweet.mediaItems && tweet.mediaItems.length > 0) {
    tweetBodyProps.mediaItems = tweet.mediaItems;
  }
  if (quotedTweetId) {
    tweetBodyProps.quotedTweetId = quotedTweetId;
  }
  if (metadataRecord) {
    tweetBodyProps.metadata = metadataRecord;
  }
  if (tweet.mediaMetadata) {
    tweetBodyProps.mediaMetadata = tweet.mediaMetadata;
  }
  if (highlightCardUrl) {
    tweetBodyProps.highlightCardUrl = highlightCardUrl;
  }
  if (mentionProfileLookup) {
    tweetBodyProps.mentionProfiles = mentionProfileLookup;
  }
  if (onFullTweetLoaded) {
    tweetBodyProps.onFullTweetLoaded = onFullTweetLoaded;
  }

  const tweetActionsProps: Parameters<typeof TweetActions>[0] = {
    metrics,
    states,
    onReply: handlers.reply,
    onRetweet: handlers.retweet,
    onLike: handlers.toggleLike,
    onBookmark: handlers.toggleBookmark,
    onCopyLink: handlers.copyLink,
    onShare: handlers.share,
    enableActions,
    compact,
  };
  if (typeof conversationCount === 'number') {
    tweetActionsProps.conversationCount = conversationCount;
  }
  if (openConversationHandler) {
    tweetActionsProps.onOpenConversation = openConversationHandler;
  }

  return (
    <div
      className={cn(
        'max-w-full',
        compact ? 'overflow-hidden' : 'overflow-visible',
        !disableHover && 'hover:bg-muted/10 dark:hover:bg-muted/20',
        canOpenConversation && 'cursor-pointer',
        className
      )}
      {...interactiveProperties}
      {...hoverProperties}
    >
      <div className="w-full text-left">
        <div className={cn('flex gap-2 sm:gap-3', compact ? 'px-3 py-3' : 'px-3 sm:px-4 py-3')}>
          <div className="flex-shrink-0">
            <TweetAvatar author={author} />
          </div>

          <div className="flex-1 min-w-0">
            {showPinnedBadge && <TweetPinnedBadge className="mb-1" />}
            <TweetHeader
              author={author}
              createdAt={tweet.createdAt}
              tweetId={tweet.id}
              compact={compact}
              isMuted={isMuted}
              onToggleMute={handlers.toggleMute}
              {...(openConversationHandler ? { onOpenConversation: openConversationHandler } : {})}
              {...(onRefreshTweet ? { onRefreshTweet } : {})}
              canSummarize={canSummarize}
              isSummarizingSummary={isSummarizing}
              canExplain={canExplain}
              onExplain={handleExplain}
              isExplaining={isExplaining}
              showDebugIds={allowAdvancedSummaryOptions}
              onMenuOpenChange={(open) => {
                updateConversationBlocker('tweet-menu', open);
              }}
            />

            <TweetBody {...tweetBodyProps} />

            <TweetSummarySection
              allowAdvancedSummaryOptions={allowAdvancedSummaryOptions}
              shouldRenderSummaryControls={shouldRenderSummaryControls}
              summarizeButtonLabel={summarizeButtonLabel}
              isSummarizing={isSummarizing}
              summaryState={summaryState}
              isChatEnabled={isChatEnabled}
              onSummaryChat={chatFromSummary}
              explanationState={explanationState}
              isExplaining={isExplaining}
              summaryMode={summaryMode}
              runActiveMode={runActiveMode}
              onSummarize={handlePrimarySummaryAction}
              settingsOpen={settingsOpen}
              openSettings={openSettings}
              closeSettings={closeSettings}
              submitSettings={submitSettings}
              draftSummaryLengthIndex={draftSummaryLengthIndex}
              setDraftSummaryLengthIndex={setDraftSummaryLengthIndex}
              draftModel={draftModel}
              setDraftModel={setDraftModel}
              canonicalDraftModel={canonicalDraftModel}
              draftCustomPrompt={draftCustomPrompt}
              setDraftCustomPrompt={setDraftCustomPrompt}
              draftSummarySearchEnabled={draftSummarySearchEnabled}
              setDraftSummarySearchEnabled={setDraftSummarySearchEnabled}
              draftSummaryMode={draftSummaryMode}
              setDraftSummaryMode={setDraftSummaryMode}
              supportsSummarySearch={supportsSummarySearch}
              liveSearchProviderName={liveSearchProviderName}
              providerHandlesVideo={providerHandlesVideo}
              renderControlsInline={shouldRenderInlineSummaryControls}
              tweetAuthor={author}
            />

            {quotedTweetForDisplay && <QuoteTweet tweet={quotedTweetForDisplay} inlineTweetComponent={InlineTweet} />}

            {suggestionTooltip ? (
              <div className="mt-3 flex justify-end">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                      aria-label="Why am I seeing this tweet?"
                    >
                      <CircleHelp className="h-3.5 w-3.5" />
                      <span>Why?</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm leading-relaxed">
                    <p className="font-semibold text-foreground">{suggestionTooltip.title}</p>
                    <p className="mt-1 text-muted-foreground">{suggestionTooltip.description}</p>
                    {suggestionTooltip.sourceLabel ? (
                      <p className="mt-2 text-xs uppercase tracking-[0.16em] text-muted-foreground/80">
                        Source: {suggestionTooltip.sourceLabel}
                      </p>
                    ) : null}
                    {suggestionTooltip.fetchedLabel ? (
                      <p className="mt-1 text-xs text-muted-foreground/80">Captured {suggestionTooltip.fetchedLabel}</p>
                    ) : null}
                  </TooltipContent>
                </Tooltip>
              </div>
            ) : null}

            <TweetActions {...tweetActionsProps} />
          </div>
        </div>
      </div>
    </div>
  );
}
